import { Box, Grid } from "@mui/material";
import type { RaceQuery } from "../../../generated/graphql";
import styles from './RaceResults.module.css'
import tabWin from '../../../assets/tab_win_hdr.png'
import tabPlc from '../../../assets/tab_plc_hdr.png'
import clsx from "clsx";
import { EXOTIC_TYPE_MAP } from "../../../constants/constants";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import {v4 as uuidv4} from 'uuid';
import useAppState from "../../../hooks/useAppState";

interface Props {
  race: null | Omit<NonNullable<RaceQuery['race']>, '__typename'>;
}

type DeductionsRunners = {
  no: number | null,
  name: string | null,
  jockey: string | null,
  jockey_changed: boolean | null,
  trainer: string | null,
  scr: boolean | null,
  late_scr: boolean | null,
  price_open: number | null,
  price: number | null,
  price_flucs: number[] | null,
  wsp_count: number | null,
  updated_ts: number | null,
  vba_price: number | null
}[]

type DeductionsResult = {
  plc: number,
  no: number,
  name: string,
  div_win_v: number,
  div_win_q: number,
  div_win_n: number,
  div_plc_v: number,
  div_plc_q: number,
  div_plc_n: number,
}

type DeductionsType = {
  meeting_id: string,
  meeting_id_gbs: number,
  event_id: string,
  m_code_4char: string,
  state: string,
  state_op_status: boolean,
  date: string,
  start_time: string,
  venue: string,
  no: string,
  name: string,
  status: string,
  status_loading: string | null,
  track_cond: string,
  track_rtg: number,
  weather: string,
  updated_ts: number,
  results: DeductionsResult | null,
  deductions: string,
  runners: DeductionsRunners | null,
  op_mkt_pct: number
}[] | []

type State = "NSW" | "QLD" | "VIC";

export default function RaceResults({ race }: Props) {

  const { controllerState } = useAppState();
  const [deductions, setDeductions] = useState<DeductionsType>()
  const [deductionSet, setDeductionSet] = useState<DeductionsType[number]>()

  useEffect(() => {
    getDeductions().then((a) => {
      setDeductions(a)
    })
  }, [race])

  const findDeductions = useCallback(() => {
    const meetingId = race?.meeting.externalIDs?.find((a) => a?.source === 'gbs')?.id
    if (!meetingId || !deductions) return null
    const eventToUse = deductions.find((a) => JSON.stringify(a.meeting_id_gbs) === meetingId && a.no === JSON.stringify(race.number))
    // eslint-disable-next-line no-console
    console.log(eventToUse)
    setDeductionSet(eventToUse)
  }, [deductions, race?.meeting.externalIDs, race?.number])

  useEffect(() => {
    findDeductions()
  }, [deductions, findDeductions])

  function findRunnerSilk(tabNo: number) {
    const silk = race?.competitors.find((a) => a.tabNo === tabNo)?.silksURL
    return silk || ''
  }

  function findRunnerName(tabNo: number) {
    const name = race?.competitors.find((a) => a.tabNo === tabNo)?.name
    return name || ''
  }

  function findRunnerBarrier(tabNo: number) {
    const barrier = race?.competitors.find((a) => a.tabNo === tabNo)?.barrier
    return barrier || 0
  }

  const toOrdinalSuffix = (num: number) => {
    const int = num,
      digits = [int % 10, int % 100],
      ordinals = ['st', 'nd', 'rd', 'th'],
      oPattern = [1, 2, 3, 4],
      tPattern = [11, 12, 13, 14, 15, 16, 17, 18, 19];
    return oPattern.includes(digits[0]) && !tPattern.includes(digits[1])
      ? int + ordinals[digits[0] - 1]
      : int + ordinals[3];
  }

  function findPrices(tabNo: number, type: "WIN" | "PLACE") {
    // const state = race?.meeting.track.state as "NSW" | "QLD" | "VIC" || "NSW"
    const state: State = (["NSW", "QLD", "VIC"] as const).includes(controllerState as State)
      ? (controllerState as State)
      : "NSW";
    const stateMapping = {
      "VIC": "V",
      "NSW": "N",
      "QLD": "Q"
    }
    const competitor = race?.competitors.find((a) => a.tabNo === tabNo)
    const fixedPrice = competitor?.prices.find((a) => a.type === type && a.type && a.source === stateMapping[state])?.price || 0
    const formatted = fixedPrice.toFixed(1);

    return formatted.endsWith('.0')
      ? formatted.slice(0, -2)
      : formatted;
  }

  const resultsToUse = useCallback(() => {
    // const state = race?.meeting.track.state as "NSW" | "QLD" | "VIC" || "NSW"
    const state: State = (["NSW", "QLD", "VIC"] as const).includes(controllerState as State)
      ? (controllerState as State)
      : "NSW";
    const stateMapping = {
      "VIC": "V",
      "NSW": "N",
      "QLD": "Q"
    }
    const results = race?.results.find((a) => a.source === stateMapping[state])
    return results
  }, [controllerState, race?.results]);

  function findTopFluc(tabNo: number) {
    const results = race?.results.find((a) => a.source === "OP")?.dividends
    const topFluc = results?.find((a) => a.type === "TOP_FLUC" && a.dividendNumbers[0] === tabNo)?.dividend || ''
    return topFluc
  }

  function findSP(tabNo: number) {
    const results = race?.results.find((a) => a.source === "OP")?.dividends
    const sp = results?.find((a) => a.type === "STARTING_PRICE" && a.dividendNumbers[0] === tabNo)?.dividend || ''
    return sp
  }

  async function getDeductions() {
    if (process.env.REACT_APP_OFFICIAL_PRICE_FEED && process.env.REACT_APP_CLIENT_DETAILS) {
      const data = await axios.get(`${process.env.REACT_APP_OFFICIAL_PRICE_FEED}?key=${encodeURIComponent(process.env.REACT_APP_CLIENT_DETAILS)}`)
      return data.data.event
    } else {
      return null
    }
  }

  const composedRunnerResults = useMemo(() => {
    let opResults = race?.results.find((r) => r.source === 'OP');

    if (!opResults) return null;

    if (opResults.positions.length < 4) {
      const results = resultsToUse()
      const newPositions = results?.positions
      opResults = {
        ...opResults,
        positions: newPositions ? newPositions : opResults.positions
      };
    }

    return opResults
  }, [race?.results, resultsToUse])

  if (!race) return null;

  return (
    <div style={{ 'fontSize': '2.6rem' }}>
      <Box sx={{ flexGrow: 0 }}>
      <Grid container spacing={0} columns={24} style={{ 'backgroundColor': 'lightGrey' }} id="runnerHeader">
        <Grid xs={16} sx={{ pl: 3 }}>
          <div className={styles.tableHeader}>
            <span style={{ 'marginRight': '5rem' }}>
              Track: {race.trackCondition}
            </span>
            <span>
              Weather: {race.weather}
            </span>
          </div>
        </Grid>
        <Grid xs={4}>
          <div className={styles.tableHeader}>
            <span style={{'marginRight': '3rem', 'minWidth': '80px', 'fontSize': '3.4rem'}}>
              TF
            </span>
            <span style={{'marginRight': '3rem', 'minWidth': '80px', 'fontSize': '3.4rem'}}>
              SP
            </span>
          </div>
        </Grid>
        <Grid xs={2} sx={{ display: 'flex' }}>
          <img src={tabWin} alt={'tab win'} className={styles.image}/>
        </Grid>
        <Grid xs={2} sx={{ display: 'flex' }}>
          <img src={tabPlc} alt={'tab plc'} className={styles.image}/>
        </Grid>
      </Grid>
      <Grid container xs={0} columns={24}>
        <Grid xs={16}>
          {/* {race.results.find((a) => a.source === "OP")?.positions.map((runner) => { */}
          {composedRunnerResults?.positions.map((runner) => {
            return (
              <Box key={runner.tabNo} className={styles.competitorRow}>
                <div className={clsx(styles[`finishingPosition-${runner.position}`])}>{toOrdinalSuffix(runner.position)}</div>
                <div style={{ 'width': '5rem', 'textAlign': 'right', 'marginRight': '1rem' }}>{runner.tabNo}</div>
                <img src={findRunnerSilk(runner.tabNo)} alt={'jockey silk'} width={'50'}/>
                <div>{findRunnerName(runner.tabNo)} ({findRunnerBarrier(runner.tabNo)})</div>
              </Box>
            )
          })}
        </Grid>
        <Grid xs={4}>
          <div className={styles.opFlucs}>  
            {/* {race.results.find((a) => a.source === "OP")?.positions.map((runner) => { */}
            {composedRunnerResults?.positions.map((runner) => {
              return (
                <Box key={runner.tabNo} className={styles.competitorRow}>
                  <div style={{'marginRight': '3rem', 'minWidth': '80px'}}>{findTopFluc(runner.tabNo)}</div>
                  <div style={{'marginRight': '3rem', 'minWidth': '80px'}}>{findSP(runner.tabNo)}</div>
                </Box>
              )
            })}
          </div>
        </Grid>
        <Grid xs={2}>
          <div style={{'height': '6.2rem'}}>
            {/* {race.results.find((a) => a.source === "OP")?.positions.map((runner) => { */}
            {composedRunnerResults?.positions.map((runner) => {
              return (
                <Box key={runner.tabNo} className={clsx(styles.competitorRow, styles.winOdds, styles.tabOdds, styles[`stateColor-${controllerState}`])}>
                  <div>
                    {runner.position === 1 ? findPrices(runner.tabNo, "WIN") : null}
                  </div>
                </Box>
              )
            })}
          </div>
        </Grid>
        <Grid xs={2}>
          <div style={{'height': '6.2rem'}}>
            {/* {race.results.find((a) => a.source === "OP")?.positions.map((runner) => { */}
            {composedRunnerResults?.positions.map((runner) => {
              return (
                <Box key={runner.tabNo} className={clsx(styles.competitorRow, styles.tabOdds, 
                styles.plcOdds, styles.placeColor)}>
                  <div>
                    {findPrices(runner.tabNo, "PLACE")}
                  </div>
                </Box>
              )
            })}
          </div>
        </Grid>
      </Grid>
    </Box>
      <div className={styles.exoticResults}>
        <div>
          <div>
            Late Scratchings / Deductions
          </div>
          <div>
            {deductions ? (
              <div>
                {deductionSet ? (
                  <>
                    {/*<div>{deductionSet.deductions || "No Deductions"}</div>*/}
                    <div>
                      Late Scr: {deductionSet.runners?.filter((r) => r.late_scr).map((a, index) => {
                        if (deductionSet.runners?.filter((r) => r.late_scr).length === index + 1) return `${a.no}`
                        else return `${a.no}, `
                      })}
                    </div>
                  </>
                ) : <></>}
              </div>
            ) : <></>}
          </div>
        </div>
        <div>
          {resultsToUse()?.dividends.filter((a) => a.type !== "WIN" && a.type !== "PLACE").map((d) => {
            return (
              <div key={d.dividendNumbers[0] + d.type + uuidv4()} className={styles.dividends}>
                <div>
                  {EXOTIC_TYPE_MAP[d.type]}
                </div>
                <div>
                  {d.dividendNumbers.join(' - ')}
                </div>
                <div style={{'textAlign': 'right'}}>
                  {d.dividend.toFixed(2)}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}